import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { catchError, filter, Observable, switchMap, take, throwError, BehaviorSubject, from } from 'rxjs';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { Router } from '@angular/router';
import { AuthenticationService } from '@app/services/authentication.service';
import { SecureStorageService } from '@app/services/secure-storage.service';
import { User } from '@app/models/user';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private snackbarService: SnackbarService,
    private authService: AuthenticationService,
    private router: Router,
    private secureStorage: SecureStorageService
  ) {}
  isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(catchError(err => {
        if (err.status === 401) {
            return this.handle401Error(request, next);
        }
        const error = err.error.message || err.statusText;
        if (err.error.message && err.status === 400) {
          this.snackbarService.show('Error', error, 'error');
        } else {
            this.snackbarService.show('Error', 'Something went wrong try again.', 'error');
        }
        return throwError(() => error);
    }));
}

private addToken(req: HttpRequest<any>, token: string) {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
}

private handle401Error(req: HttpRequest<any>, next: HttpHandler) {
  if (!this.isRefreshing) {
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);
    return this.authService.refreshToken().pipe(
      switchMap((response: any) => {
        this.isRefreshing = false;
        // After refresh, get updated user from secure storage (async)
        return from(this.secureStorage.getItem<User>('currentUser', false)).pipe(
          switchMap((user) => {
            if (user && user.token) {
              this.refreshTokenSubject.next(user.token);
              this.authService.updateCurrentUser(user);
              return next.handle(this.addToken(req, user.token));
            }
            // If token refresh failed, logout
            this.authService.logout();
            return throwError(() => new Error('Token refresh failed'));
          })
        );
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.authService.logout();
        return throwError(() => err);
      })
    );
  } else {
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next.handle(this.addToken(req, token!)))
    );
  }
}
}
